import { ChevronDownIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Bars3Icon } from "@heroicons/react/24/solid";
import React, {
  type ChangeEvent,
  type DragEvent,
  type FC,
  useEffect,
  useMemo,
  useState,
} from "react";

import { DarkThemeSingleSelect } from "../../../../components";
import { VoiceInput } from "../shared";
import {
  getReportBuilderSelectStyles,
  REPORT_BUILDER_SELECT_COMPONENTS,
} from "../shared/rebootLiveSelectStyles";

import {
  ANALYSIS_STRUCTURE_OPTIONS,
  type ReportOption,
  VISUALIZATION_OPTIONS,
} from "./analysisBuilderConstants";

export interface ReportSectionConfig {
  metricsFocus: string;
  analysisStructure: string;
  visualization: string;
}

export interface ReportSection {
  id: string;
  title: string;
  config: ReportSectionConfig;
  description: string;
}

interface ReportSectionCardProps {
  section: ReportSection;
  sectionNumber: number;
  onUpdate: (updates: Partial<ReportSection>) => void;
  onRemove: () => void;
  canRemove: boolean;
  totalSections: number;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isDisabled?: boolean;
  metricsFocusOptions: ReportOption[];
  isMetricsFocusDisabled?: boolean;
}

export const ReportSectionCard: FC<ReportSectionCardProps> = ({
  section,
  sectionNumber,
  onUpdate,
  onRemove,
  canRemove,
  totalSections,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isCollapsed,
  onToggleCollapse,
  isDisabled = false,
  metricsFocusOptions,
  isMetricsFocusDisabled = false,
}) => {
  const [description, setDescription] = useState(section.description);

  // Sync local description with section prop
  useEffect(() => {
    setDescription(section.description);
  }, [section.description]);

  const showCollapseButton = totalSections > 1;

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onUpdate({ title: e.target.value });
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    onUpdate({ description: newDescription });
  };

  const handleVoiceTranscript = (transcript: string) => {
    const newDescription = description + transcript;
    setDescription(newDescription);
    onUpdate({ description: newDescription });
  };

  const handleConfigChange = (
    field: keyof ReportSectionConfig,
    value: string,
  ) => {
    onUpdate({
      config: {
        ...section.config,
        [field]: value,
      },
    });
  };

  const configSelectStyles = useMemo(() => getReportBuilderSelectStyles(), []);

  return (
    <div
      className="bg-[#1d1d1f] border border-white/10 rounded-lg shadow-[0px_4px_10px_0px_rgba(0,0,0,0.1)]"
      data-testid={`report-section-card-${section.id}`}
      data-section-number={sectionNumber}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="p-6 flex flex-col gap-6">
        {/* Section Header Row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Bars3Icon className="w-5 h-5 text-[#979797] cursor-grab flex-shrink-0" />
            <input
              type="text"
              value={section.title}
              onChange={handleTitleChange}
              readOnly={isCollapsed || isDisabled}
              disabled={isDisabled}
              placeholder="Give this section a name (e.g., Shoulder Mechanics, Velocity Profile...)"
              data-testid={`report-section-card-title-${section.id}`}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-[#979797] focus:outline-none focus:border-[#fb6a07] font-['Inter'] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex items-center gap-2">
            {showCollapseButton && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="p-1 hover:bg-[#252527] rounded transition-colors"
              >
                <ChevronDownIcon
                  className={`w-4 h-4 text-[#979797] transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                />
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="p-2 hover:bg-[#252527] rounded-md transition-colors"
                title="Remove section"
                data-testid={`report-section-card-delete-button-${sectionNumber}`}
              >
                <TrashIcon className="w-4 h-4 text-red-500 hover:text-red-400" />
              </button>
            )}
          </div>
        </div>

        {!isCollapsed && (
          <>
            {/* Config Dropdowns */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[#b6b2b2] text-sm block mb-2">
                  Metrics & Focus
                </label>
                <DarkThemeSingleSelect
                  value={section.config.metricsFocus}
                  onChange={(value) =>
                    handleConfigChange("metricsFocus", value)
                  }
                  options={metricsFocusOptions}
                  placeholder={
                    isMetricsFocusDisabled
                      ? "Select a movement type first"
                      : "Select a metric category"
                  }
                  styles={configSelectStyles}
                  customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
                  isDisabled={isDisabled || isMetricsFocusDisabled}
                  classNamePrefix={`report-section-metrics-${section.id}`}
                />
              </div>
              <div className="flex-1">
                <label className="text-[#b6b2b2] text-sm block mb-2">
                  Analysis Structure
                </label>
                <DarkThemeSingleSelect
                  value={section.config.analysisStructure}
                  onChange={(value) =>
                    handleConfigChange("analysisStructure", value)
                  }
                  options={ANALYSIS_STRUCTURE_OPTIONS}
                  placeholder="Select Structure"
                  styles={configSelectStyles}
                  customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
                  isDisabled={isDisabled}
                  classNamePrefix={`report-section-structure-${section.id}`}
                />
              </div>
              <div className="flex-1">
                <label className="text-[#b6b2b2] text-sm block mb-2">
                  Visualization
                </label>
                <DarkThemeSingleSelect
                  value={section.config.visualization}
                  onChange={(value) =>
                    handleConfigChange("visualization", value)
                  }
                  options={VISUALIZATION_OPTIONS}
                  placeholder="Select Visualization"
                  styles={configSelectStyles}
                  customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
                  isDisabled={isDisabled}
                  classNamePrefix={`report-section-visualization-${section.id}`}
                />
              </div>
            </div>

            {/* Description Textarea */}
            <div className="flex flex-col gap-3">
              <label
                htmlFor={`report-section-card-description-${section.id}`}
                className="text-white font-medium"
              >
                Describe what you want to focus on.
              </label>
              <textarea
                id={`report-section-card-description-${section.id}`}
                value={description}
                onChange={handleDescriptionChange}
                placeholder="e.g., Focus on lead leg block timing, compare early vs late innings..."
                className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 text-white placeholder:text-[#979797] min-h-[120px] resize-y focus:border-[#fb6a07] focus:outline-none transition-colors font-['Inter'] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDisabled}
                data-testid={`report-section-card-description-${section.id}`}
              />

              {/* Bottom row with helper text and mic button */}
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-[#979797] flex-1">
                  The AI uses this text plus your dropdown choices to generate
                  this section of the report.
                </p>
                <VoiceInput onTranscript={handleVoiceTranscript} size="md" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
